import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Upload, FileText, Search, Sparkles, Loader2, Trash2, ArrowLeft,
  Download, BookOpen, Database, RefreshCw, ExternalLink, FileType,
} from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { thesisApi } from '@/api/thesisApi';
import { ThemeToggle } from '@/components/ThemeToggle';

export default function ThesisWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('documents');

  // PubMed state
  const [pubmedQuery, setPubmedQuery] = useState('');
  const [pubmedResults, setPubmedResults] = useState([]);
  const [pubmedSearching, setPubmedSearching] = useState(false);
  const [pubmedIngestingIds, setPubmedIngestingIds] = useState(new Set());

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Chapter state
  const [chapterDialogOpen, setChapterDialogOpen] = useState(false);
  const [chapterForm, setChapterForm] = useState({ title: '', prompt: '', n_chunks: 10 });
  const [generating, setGenerating] = useState(false);
  const [openChapterId, setOpenChapterId] = useState(null);

  useEffect(() => {
    if (!authStorage.isAuthorAuthenticated()) {
      navigate('/thesis/login');
      return;
    }
    setUser(authStorage.getAuthorUser());
  }, [navigate]);

  const loadAll = useCallback(async () => {
    if (!user?.id || !projectId) return;
    setLoading(true);
    try {
      const [p, d, ch] = await Promise.all([
        thesisApi.getProject(user.id, projectId),
        thesisApi.listDocuments(user.id, projectId),
        thesisApi.listChapters(user.id, projectId),
      ]);
      setProject(p);
      setDocs(d.documents || []);
      setChapters(ch.chapters || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) toast.error('Проектът не е намерен');
      else if (status === 403) toast.error('Нямате достъп до този проект');
      else toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
      navigate('/thesis');
    } finally {
      setLoading(false);
    }
  }, [user, projectId, navigate]);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  // ---- DOCUMENT UPLOAD ----
  const handleFileUpload = async (e, sourceType = 'upload') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await thesisApi.uploadDocument(user.id, projectId, file, null, sourceType);
      const kind = sourceType === 'style_reference' ? 'стилов референт' : 'документ';
      toast.success(`"${file.name}" е качен като ${kind}`);
      loadAll();
    } catch (err) {
      toast.error('Качването пропадна: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ---- TABLE GENERATION (feature F) ----
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableTopic, setTableTopic] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  const [generatedTable, setGeneratedTable] = useState(null);

  const handleGenerateTable = async () => {
    if (!tableTopic.trim()) { toast.error('Въведете тема'); return; }
    setTableLoading(true);
    try {
      const res = await thesisApi.generateTable(user.id, projectId, tableTopic, 8, project?.language || 'bg');
      setGeneratedTable(res);
      toast.success('Таблицата е готова');
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTableLoading(false);
    }
  };

  // ---- VERSION HISTORY (feature H) ----
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsOpenFor, setVersionsOpenFor] = useState(null);

  const loadVersions = async (chapterId) => {
    setVersionsLoading(true);
    setVersionsOpenFor(chapterId);
    try {
      const data = await thesisApi.listVersions(user.id, chapterId);
      setVersions(data.versions || []);
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRestoreVersion = async (chapterId, versionId) => {
    if (!window.confirm('Връщане към тази версия? Текущата ще се запише в историята.')) return;
    try {
      await thesisApi.restoreVersion(user.id, chapterId, versionId);
      toast.success('Версията е възстановена');
      setVersionsOpenFor(null);
      loadAll();
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- AI REVIEWER MODE ----
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewOpenFor, setReviewOpenFor] = useState(null);
  const [applyingIssue, setApplyingIssue] = useState(null);

  const handleRunReview = async (chapterId) => {
    setReviewLoading(true);
    setReviewOpenFor(chapterId);
    setReview(null);
    try {
      const data = await thesisApi.reviewChapter(user.id, chapterId);
      setReview(data);
      toast.success(`Рецензия готова: ${data.overall_score}/10`);
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
      setReviewOpenFor(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleApplySuggestion = async (issueIndex) => {
    if (!review?.id || !reviewOpenFor) return;
    setApplyingIssue(issueIndex);
    try {
      await thesisApi.applySuggestion(user.id, reviewOpenFor, review.id, issueIndex);
      toast.success('Препоръката е приложена. Главата е обновена.');
      // mark applied locally so UI re-renders without closing the dialog
      setReview((r) => {
        if (!r) return r;
        const issues = [...(r.issues || [])];
        if (issues[issueIndex]) issues[issueIndex] = { ...issues[issueIndex], applied: true };
        return { ...r, issues };
      });
      // Refresh chapters list (and selected chapter) in the background
      loadAll();
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setApplyingIssue(null);
    }
  };

  const handleDeleteDoc = async (docId, title) => {
    if (!window.confirm(`Премахване на "${title}"?`)) return;
    try {
      await thesisApi.deleteDocument(user.id, projectId, docId);
      toast.success('Документът е премахнат');
      loadAll();
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- PUBMED ----
  const handlePubmedSearch = async () => {
    if (!pubmedQuery.trim()) {
      toast.error('Въведете заявка');
      return;
    }
    setPubmedSearching(true);
    setPubmedResults([]);
    try {
      const data = await thesisApi.pubmedSearch(user.id, projectId, pubmedQuery, 15, false);
      setPubmedResults(data.articles || []);
      toast.success(`${data.count} статии намерени`);
    } catch (e) {
      toast.error('PubMed грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPubmedSearching(false);
    }
  };

  const handleIngestPubmed = async (pmid) => {
    setPubmedIngestingIds((prev) => new Set(prev).add(pmid));
    try {
      const res = await thesisApi.pubmedIngest(user.id, projectId, [pmid]);
      if (res.ingested > 0) {
        toast.success('Статията е добавена към проекта');
        loadAll();
      } else {
        toast.info('Статията вече е добавена');
      }
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPubmedIngestingIds((prev) => {
        const s = new Set(prev);
        s.delete(pmid);
        return s;
      });
    }
  };

  const handleIngestAll = async () => {
    const pmids = pubmedResults.map((a) => a.pmid).filter(Boolean);
    if (!pmids.length) return;
    setPubmedSearching(true);
    try {
      const res = await thesisApi.pubmedIngest(user.id, projectId, pmids);
      toast.success(`Добавени: ${res.ingested}, пропуснати: ${res.skipped_duplicates}`);
      loadAll();
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPubmedSearching(false);
    }
  };

  // ---- CHAPTER GENERATION ----
  const handleGenerateChapter = async () => {
    if (!chapterForm.title.trim()) {
      toast.error('Заглавието на главата е задължително');
      return;
    }
    if (docs.length === 0) {
      toast.error('Качете поне един документ или ингестирайте PubMed статии');
      return;
    }
    setGenerating(true);
    try {
      const ch = await thesisApi.generateChapter(
        user.id, projectId,
        chapterForm.title, chapterForm.prompt,
        chapterForm.n_chunks, project?.language || 'bg',
      );
      toast.success(`Главата "${ch.title}" е генерирана (${ch.citations.length} цитата)`);
      setChapterDialogOpen(false);
      setChapterForm({ title: '', prompt: '', n_chunks: 10 });
      setOpenChapterId(ch.id);
      setTab('chapters');
      loadAll();
    } catch (e) {
      toast.error('Генериране пропадна: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteChapter = async (chapterId, title) => {
    if (!window.confirm(`Изтриване на главата "${title}"?`)) return;
    try {
      await thesisApi.deleteChapter(user.id, chapterId);
      toast.success('Главата е изтрита');
      if (openChapterId === chapterId) setOpenChapterId(null);
      loadAll();
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- EXPORTS ----
  const exportAuthor = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : (user?.username || 'Author');

  if (loading || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const openChapter = chapters.find((c) => c.id === openChapterId) || chapters[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-background to-background dark:from-slate-950 dark:via-background text-foreground thesis-brand">
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" asChild data-testid="back-to-projects">
              <Link to="/thesis"><ArrowLeft className="w-4 h-4 mr-1" /></Link>
            </Button>
            <Link to="/thesis" className="flex items-center gap-2 shrink-0" data-testid="thesis-logo-mini">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-700 to-amber-600 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-bold text-sm hidden md:inline">ThesisRAG</span>
            </Link>
            <span className="text-muted-foreground hidden md:inline">/</span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate" data-testid="thesis-workspace-title">{project.title}</h1>
              <p className="text-xs text-muted-foreground truncate">
                {project.document_count || 0} документа · {project.chapter_count || 0} глави · {project.vector_stats?.total_chunks || 0} chunks
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline">{project.language === 'bg' ? 'BG' : 'EN'}</Badge>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="documents" data-testid="tab-documents">
              <FileText className="w-4 h-4 mr-2" />Документи ({docs.length})
            </TabsTrigger>
            <TabsTrigger value="pubmed" data-testid="tab-pubmed">
              <Search className="w-4 h-4 mr-2" />PubMed търсене
            </TabsTrigger>
            <TabsTrigger value="chapters" data-testid="tab-chapters">
              <Sparkles className="w-4 h-4 mr-2" />Глави ({chapters.length})
            </TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export">
              <Download className="w-4 h-4 mr-2" />Експорт
            </TabsTrigger>
          </TabsList>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-teal-700 dark:text-teal-400" />
                    Доказателствен документ
                  </CardTitle>
                  <CardDescription>PDF / DOCX / MD / TXT — служи като източник на факти и цитати</CardDescription>
                </CardHeader>
                <CardContent>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.docx,.md,.txt"
                      onChange={(e) => handleFileUpload(e, 'upload')} disabled={uploading}
                      className="hidden" data-testid="upload-doc-input" />
                    <Button asChild disabled={uploading} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
                      <span>
                        {uploading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Качване...</>
                          : <><Upload className="w-4 h-4 mr-2" />Качи доказателствен документ</>}
                      </span>
                    </Button>
                  </label>
                </CardContent>
              </Card>

              <Card className="border-amber-300/50 dark:border-amber-700/50 bg-amber-50/30 dark:bg-amber-950/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                    Стилов референт <Badge variant="outline" className="text-xs">A</Badge>
                  </CardTitle>
                  <CardDescription>Референтна дисертация — AI ще имитира нейния глас и стил, БЕЗ да копира съдържание</CardDescription>
                </CardHeader>
                <CardContent>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.docx,.md,.txt"
                      onChange={(e) => handleFileUpload(e, 'style_reference')} disabled={uploading}
                      className="hidden" data-testid="upload-style-input" />
                    <Button asChild disabled={uploading} variant="outline" className="w-full border-amber-600 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-950">
                      <span>
                        {uploading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Качване...</>
                          : <><Upload className="w-4 h-4 mr-2" />Качи стилов референт</>}
                      </span>
                    </Button>
                  </label>
                </CardContent>
              </Card>
            </div>

            {docs.length === 0 ? (
              <Card className="text-center py-12 border-dashed">
                <CardContent>
                  <Database className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Все още няма индексирани документи</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="docs-list">
                {docs.map((d) => (
                  <Card key={d.id} data-testid={`doc-card-${d.id}`}>
                    <CardContent className="pt-5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-medium leading-snug line-clamp-2">{d.title}</h3>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(d.id, d.title)}
                          data-testid={`doc-delete-${d.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge variant={d.source_type === 'style_reference' ? 'default' : 'secondary'}
                          className={d.source_type === 'style_reference' ? 'bg-amber-600' : ''}>
                          {d.source_type === 'style_reference' ? 'стилов референт' : d.source_type}
                        </Badge>
                        {d.pmid && (
                          <a href={`https://pubmed.ncbi.nlm.nih.gov/${d.pmid}/`} target="_blank" rel="noopener noreferrer">
                            <Badge variant="outline" className="hover:bg-accent">PMID: {d.pmid}</Badge>
                          </a>
                        )}
                        {d.doi && (
                          <a href={`https://doi.org/${d.doi}`} target="_blank" rel="noopener noreferrer">
                            <Badge variant="outline" className="hover:bg-accent">DOI</Badge>
                          </a>
                        )}
                        <Badge variant="outline">{d.chunk_count} chunks</Badge>
                      </div>
                      {d.authors?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                          {d.authors.slice(0, 3).join(', ')}{d.authors.length > 3 && '...'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PUBMED TAB */}
          <TabsContent value="pubmed" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Търсене в PubMed</CardTitle>
                <CardDescription>Намерете и добавете медицинска литература директно от NCBI.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Напр. breast cancer screening dense breasts MRI"
                    value={pubmedQuery}
                    onChange={(e) => setPubmedQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePubmedSearch()}
                    data-testid="pubmed-query-input"
                  />
                  <Button onClick={handlePubmedSearch} disabled={pubmedSearching} data-testid="pubmed-search-btn">
                    {pubmedSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {pubmedResults.length > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{pubmedResults.length} резултата</span>
                    <Button size="sm" variant="outline" onClick={handleIngestAll} disabled={pubmedSearching}
                      data-testid="ingest-all-btn">
                      <Database className="w-3 h-3 mr-1" /> Добави всички
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {pubmedResults.length > 0 && (
              <div className="space-y-3" data-testid="pubmed-results">
                {pubmedResults.map((a) => {
                  const isIngesting = pubmedIngestingIds.has(a.pmid);
                  const alreadyAdded = docs.some((d) => d.pmid === a.pmid);
                  return (
                    <Card key={a.pmid} data-testid={`pubmed-result-${a.pmid}`}>
                      <CardContent className="pt-5">
                        <h3 className="font-medium mb-1">{a.title}</h3>
                        <p className="text-xs text-muted-foreground mb-2">
                          {a.authors?.slice(0, 5).join(', ')}{a.authors?.length > 5 && '...'} · {a.journal} · {a.year}
                        </p>
                        {a.abstract && <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{a.abstract}</p>}
                        <div className="flex items-center gap-2 flex-wrap">
                          <a href={`https://pubmed.ncbi.nlm.nih.gov/${a.pmid}/`} target="_blank" rel="noopener noreferrer">
                            <Badge variant="outline" className="hover:bg-accent">
                              PMID: {a.pmid} <ExternalLink className="w-3 h-3 ml-1 inline" />
                            </Badge>
                          </a>
                          {a.doi && (
                            <a href={`https://doi.org/${a.doi}`} target="_blank" rel="noopener noreferrer">
                              <Badge variant="outline" className="hover:bg-accent">DOI</Badge>
                            </a>
                          )}
                          {alreadyAdded ? (
                            <Badge variant="secondary">✓ Добавена</Badge>
                          ) : (
                            <Button size="sm" variant="default" onClick={() => handleIngestPubmed(a.pmid)}
                              disabled={isIngesting} data-testid={`ingest-pubmed-${a.pmid}`}>
                              {isIngesting
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <><Database className="w-3 h-3 mr-1" />Добави</>}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* CHAPTERS TAB */}
          <TabsContent value="chapters" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Глави на дисертацията</CardTitle>
                  <CardDescription>RAG-генериране на глави, обосновани на индексираните източници.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog open={tableDialogOpen} onOpenChange={(o) => { setTableDialogOpen(o); if (!o) setGeneratedTable(null); }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" disabled={docs.length === 0} data-testid="generate-table-btn">
                        <FileType className="w-4 h-4 mr-2" /> Генерирай таблица
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Генериране на таблица с данни</DialogTitle>
                        <DialogDescription>
                          AI извлича количествени резултати от източниците и ги форматира като markdown таблица с цитати.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <Input value={tableTopic}
                          onChange={(e) => setTableTopic(e.target.value)}
                          placeholder="Напр. Чувствителност на MRI vs мамография"
                          data-testid="table-topic-input" />
                        <Button onClick={handleGenerateTable} disabled={tableLoading} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
                          {tableLoading
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Генериране...</>
                            : <><Sparkles className="w-4 h-4 mr-2" />Генерирай</>}
                        </Button>
                        {generatedTable && (
                          <div className="border rounded-md p-3 bg-muted/30 max-h-96 overflow-auto" data-testid="generated-table-result">
                            <div className="text-xs text-muted-foreground mb-2">{generatedTable.chunks_used} източника</div>
                            <article className="prose prose-sm dark:prose-invert max-w-none">
                              <ReactMarkdown components={{
                                p: ({ children }) => <p>{renderCitations(children)}</p>,
                                td: ({ children }) => <td>{renderCitations(children)}</td>,
                              }}>{generatedTable.markdown}</ReactMarkdown>
                            </article>
                            <Button size="sm" variant="ghost" className="mt-2"
                              onClick={() => {
                                navigator.clipboard.writeText(generatedTable.markdown);
                                toast.success('Копирано в clipboard');
                              }}>
                              Копирай markdown
                            </Button>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={chapterDialogOpen} onOpenChange={setChapterDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="generate-chapter-btn" disabled={docs.length === 0}
                        className="bg-teal-700 hover:bg-teal-800 text-white">
                        <Sparkles className="w-4 h-4 mr-2" /> Генерирай глава
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Генериране на нова глава</DialogTitle>
                      <DialogDescription>
                        Главата ще бъде генерирана от GPT-5.2 на база индексираните източници в проекта.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Заглавие на главата *</label>
                        <Input value={chapterForm.title}
                          onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                          placeholder="Напр. Преглед на литературата"
                          data-testid="chapter-title-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Допълнителни инструкции</label>
                        <Textarea value={chapterForm.prompt}
                          onChange={(e) => setChapterForm({ ...chapterForm, prompt: e.target.value })}
                          placeholder="Фокусирай се върху чувствителност vs специфичност..."
                          rows={3}
                          data-testid="chapter-prompt-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Брой източници в контекста: {chapterForm.n_chunks}</label>
                        <input type="range" min={4} max={20} value={chapterForm.n_chunks}
                          onChange={(e) => setChapterForm({ ...chapterForm, n_chunks: parseInt(e.target.value, 10) })}
                          className="w-full" data-testid="chapter-nchunks-slider" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setChapterDialogOpen(false)}>Отказ</Button>
                      <Button onClick={handleGenerateChapter} disabled={generating} data-testid="chapter-submit-btn"
                        className="bg-teal-700 hover:bg-teal-800 text-white">
                        {generating
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Генериране (30-90с)...</>
                          : <><Sparkles className="w-4 h-4 mr-2" />Генерирай</>}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>
            </Card>

            {chapters.length === 0 ? (
              <Card className="text-center py-12 border-dashed">
                <CardContent>
                  <Sparkles className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    {docs.length === 0
                      ? 'Първо качете документи или PubMed статии, после генерирайте глава'
                      : 'Все още няма генерирани глави'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-[280px_1fr] gap-4" data-testid="chapters-layout">
                <div className="space-y-2">
                  {chapters.map((c) => (
                    <Card key={c.id}
                      className={`cursor-pointer hover:shadow-md transition ${openChapter?.id === c.id ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => setOpenChapterId(c.id)}
                      data-testid={`chapter-item-${c.id}`}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-medium text-sm leading-snug">{c.title}</h3>
                          <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={(e) => { e.stopPropagation(); handleDeleteChapter(c.id, c.title); }}>
                            <Trash2 className="w-3 h-3 text-destructive" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {c.citations?.length || 0} цитата · {c.markdown?.length || 0} символа
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card>
                  {openChapter ? (
                    <>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <CardTitle className="font-serif">{openChapter.title}</CardTitle>
                            <CardDescription>
                              {openChapter.chunks_used} източника · {openChapter.citations?.length || 0} уникални цитата · модел: {openChapter.model_used}
                              {openChapter.style_examples_used > 0 && <> · <span className="text-amber-700 dark:text-amber-400">стил: {openChapter.style_examples_used} реф.</span></>}
                              {openChapter.previous_chapters_used > 0 && <> · {openChapter.previous_chapters_used} предишни глави в контекст</>}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button variant="outline" size="sm" onClick={() => loadVersions(openChapter.id)}
                              data-testid={`chapter-versions-${openChapter.id}`}>
                              <RefreshCw className="w-3 h-3 mr-1" /> История
                            </Button>
                            <Button variant="default" size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() => handleRunReview(openChapter.id)}
                              data-testid={`chapter-review-${openChapter.id}`}>
                              <Sparkles className="w-3 h-3 mr-1" /> AI Рецензия
                            </Button>
                          </div>
                        </div>

                        {/* Citation Verification Summary (feature B) */}
                        {openChapter.verification && (
                          <div className="mt-3 flex items-center gap-3 p-3 rounded-md bg-muted/40 border" data-testid="chapter-verification">
                            <div className={`text-2xl font-bold ${
                              openChapter.verification.summary.trust_score >= 0.9 ? 'text-green-600 dark:text-green-400' :
                              openChapter.verification.summary.trust_score >= 0.6 ? 'text-amber-600 dark:text-amber-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {Math.round(openChapter.verification.summary.trust_score * 100)}%
                            </div>
                            <div className="text-xs text-muted-foreground leading-tight">
                              <div className="font-medium text-foreground">Citation Verifier</div>
                              {openChapter.verification.summary.verified}/{openChapter.verification.summary.total} цитата проверени срещу източниците.
                              {openChapter.verification.summary.unverified > 0 && (
                                <span className="text-red-600 dark:text-red-400">
                                  {' '}({openChapter.verification.summary.unverified} непотвърдени)
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        <article className="prose prose-sm dark:prose-invert max-w-none thesis-md"
                          data-testid="chapter-content">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p className="my-3 leading-relaxed">{renderCitations(children, openChapter.verification)}</p>,
                              li: ({ children }) => <li>{renderCitations(children, openChapter.verification)}</li>,
                              td: ({ children }) => <td>{renderCitations(children, openChapter.verification)}</td>,
                            }}
                          >
                            {openChapter.markdown || '*(empty)*'}
                          </ReactMarkdown>
                        </article>

                        {openChapter.citations?.length > 0 && (
                          <div className="mt-6 pt-4 border-t border-border">
                            <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Източници в тази глава</h4>
                            <ul className="space-y-1 text-xs">
                              {openChapter.citations.map((cit, i) => (
                                <li key={i}>
                                  <span className="font-mono text-primary mr-1">{cit.manubot_ref}</span>
                                  <span>{cit.source_title}</span>
                                  {cit.doi && <a className="ml-2 text-xs underline" href={`https://doi.org/${cit.doi}`} target="_blank" rel="noopener noreferrer">DOI</a>}
                                  {cit.pmid && <a className="ml-2 text-xs underline" href={`https://pubmed.ncbi.nlm.nih.gov/${cit.pmid}/`} target="_blank" rel="noopener noreferrer">PubMed</a>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </>
                  ) : (
                    <CardContent className="py-12 text-center text-muted-foreground">
                      Изберете глава от списъка
                    </CardContent>
                  )}
                </Card>
              </div>
            )}
          </TabsContent>

          {/* EXPORT TAB */}
          <TabsContent value="export" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Експорт на дисертацията</CardTitle>
                <CardDescription>
                  Изтеглете цялата дисертация в избран формат. Цитатите се запазват като Manubot препратки (Markdown) или като линкове (HTML / DOCX).
                </CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <ExportCard
                  icon={<FileType className="w-8 h-8" />}
                  title="Manubot Markdown"
                  desc=".md с @doi:/@pmid: синтаксис"
                  href={thesisApi.exportMarkdownUrl(user.id, projectId, exportAuthor)}
                  testid="export-md"
                />
                <ExportCard
                  icon={<FileText className="w-8 h-8" />}
                  title="HTML"
                  desc="Готов за публикуване уеб документ"
                  href={thesisApi.exportHtmlUrl(user.id, projectId, exportAuthor)}
                  testid="export-html"
                />
                <ExportCard
                  icon={<Download className="w-8 h-8" />}
                  title="Word (.docx)"
                  desc="За редакция в MS Word"
                  href={thesisApi.exportDocxUrl(user.id, projectId, exportAuthor)}
                  testid="export-docx"
                />
                <ExportCard
                  icon={<FileText className="w-8 h-8" />}
                  title="PDF"
                  desc="Готов за печат / изпращане"
                  href={thesisApi.exportPdfUrl(user.id, projectId, exportAuthor)}
                  testid="export-pdf"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-serif">Библиография <Badge variant="outline" className="ml-2 text-xs">C</Badge></CardTitle>
                <CardDescription>
                  Извлича всички уникални цитати от главите и ги форматира в избран академичен стил.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <ExportCard icon={<FileText className="w-7 h-7" />} title="Vancouver"
                  desc="Стандарт за медицински журнали"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'vancouver')}
                  testid="biblio-vancouver" />
                <ExportCard icon={<FileText className="w-7 h-7" />} title="APA 7th"
                  desc="Psychology / social sciences"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'apa')}
                  testid="biblio-apa" />
                <ExportCard icon={<FileType className="w-7 h-7" />} title="BibTeX"
                  desc="За LaTeX потребители"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'bibtex')}
                  testid="biblio-bibtex" />
                <ExportCard icon={<FileType className="w-7 h-7" />} title="Manubot YAML"
                  desc="citations.yaml override"
                  href={thesisApi.bibliographyUrl(user.id, projectId, 'manubot')}
                  testid="biblio-manubot" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">За формата Manubot</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>
                  Експортът използва официалния <a className="underline" href="https://manubot.org" target="_blank" rel="noopener noreferrer">Manubot</a> цитат-синтаксис:
                  <code className="ml-1 px-1 rounded bg-muted">@doi:10.xxxx/yy</code> и
                  <code className="ml-1 px-1 rounded bg-muted">@pmid:12345</code>.
                </p>
                <p>
                  За пълно Manubot-build (PDF, web site с библиография) клонирайте Manubot template-а, поставете експортираното .md, и стартирайте <code className="px-1 rounded bg-muted">manubot process</code>.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* AI Reviewer Dialog */}
        <Dialog open={!!reviewOpenFor} onOpenChange={(o) => { if (!o) { setReviewOpenFor(null); setReview(null); } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-serif">
                <Sparkles className="w-5 h-5 text-amber-600" />
                AI Академична Рецензия
              </DialogTitle>
              <DialogDescription>
                Симулиран peer-review критик чете главата и дава конкретни препоръки.
              </DialogDescription>
            </DialogHeader>
            {reviewLoading || !review ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                <p className="text-sm text-muted-foreground">
                  Рецензентът анализира главата (30-60 сек)...
                </p>
              </div>
            ) : (
              <div className="space-y-5" data-testid="review-results">
                {/* Score */}
                <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-br from-amber-50 to-teal-50 dark:from-amber-950/30 dark:to-teal-950/30 border">
                  <div className={`text-5xl font-bold ${
                    review.overall_score >= 8 ? 'text-green-600 dark:text-green-400' :
                    review.overall_score >= 6 ? 'text-amber-600 dark:text-amber-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>{review.overall_score}<span className="text-2xl text-muted-foreground">/10</span></div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Обща оценка</div>
                    <p className="text-sm leading-relaxed">{review.summary}</p>
                  </div>
                </div>

                {/* Strengths */}
                {review.strengths?.length > 0 && (
                  <div data-testid="review-strengths">
                    <h4 className="font-semibold text-sm mb-2 text-green-700 dark:text-green-400">
                      ✓ Силни страни
                    </h4>
                    <ul className="space-y-1 text-sm">
                      {review.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-1">•</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Issues */}
                {review.issues?.length > 0 && (
                  <div data-testid="review-issues">
                    <h4 className="font-semibold text-sm mb-2">⚠ Проблеми и препоръки ({review.issues.length})</h4>
                    <div className="space-y-3">
                      {review.issues.map((iss, i) => {
                        const sev = iss.severity || 'medium';
                        const sevClass = sev === 'high'
                          ? 'border-l-red-500 bg-red-50 dark:bg-red-950/30'
                          : sev === 'medium'
                          ? 'border-l-amber-500 bg-amber-50 dark:bg-amber-950/30'
                          : 'border-l-blue-400 bg-blue-50 dark:bg-blue-950/30';
                        const sevLabel = sev === 'high' ? 'Критичен' : sev === 'medium' ? 'Среден' : 'Минор';
                        const isApplying = applyingIssue === i;
                        const isApplied = iss.applied;
                        return (
                          <div key={i} className={`border-l-4 p-3 rounded-r ${sevClass}`} data-testid={`review-issue-${i}`}>
                            <div className="flex items-center justify-between mb-1 gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Badge variant="outline" className="text-xs shrink-0">{sevLabel}</Badge>
                                {iss.where && <span className="text-xs font-mono text-muted-foreground truncate">{iss.where}</span>}
                              </div>
                              {isApplied ? (
                                <Badge variant="default" className="bg-green-600 text-white shrink-0" data-testid={`issue-applied-${i}`}>
                                  ✓ Приложено
                                </Badge>
                              ) : (
                                <Button size="sm" variant="default"
                                  className="shrink-0 bg-teal-700 hover:bg-teal-800 text-white text-xs h-7"
                                  onClick={() => handleApplySuggestion(i)}
                                  disabled={isApplying || applyingIssue !== null}
                                  data-testid={`apply-suggestion-${i}`}>
                                  {isApplying
                                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Прилагане...</>
                                    : <><Sparkles className="w-3 h-3 mr-1" />Поправи автоматично</>}
                                </Button>
                              )}
                            </div>
                            <p className="text-sm font-medium mb-1">{iss.comment}</p>
                            {iss.suggestion && (
                              <p className="text-xs text-muted-foreground italic">
                                <span className="font-semibold">Препоръка:</span> {iss.suggestion}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Version History Dialog (feature H) */}
        <Dialog open={!!versionsOpenFor} onOpenChange={(o) => { if (!o) setVersionsOpenFor(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>История на промените</DialogTitle>
              <DialogDescription>
                Запазени снимки на главата преди всяко редактиране. Можете да възстановите всяка версия.
              </DialogDescription>
            </DialogHeader>
            {versionsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : versions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Все още няма запазени версии. Първата ще се запази при следваща редакция.
              </p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="versions-list">
                {versions.map((v) => (
                  <Card key={v.id} data-testid={`version-${v.id}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-muted-foreground">
                            {new Date(v.created_at).toLocaleString('bg-BG')} · {v.note}
                          </div>
                          <div className="text-sm font-medium mt-1">{v.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {v.markdown?.slice(0, 200)}...
                          </div>
                        </div>
                        <Button size="sm" variant="outline"
                          onClick={() => handleRestoreVersion(versionsOpenFor, v.id)}
                          data-testid={`restore-${v.id}`}>
                          Възстанови
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

// ----------------------------------------------------------------------
// helpers
// ----------------------------------------------------------------------

function renderCitations(children, verification) {
  // walk react children, replace @doi:/@pmid:/@tag: tokens inside strings with links
  if (typeof children === 'string') return tokenize(children, verification);
  if (Array.isArray(children)) return children.map((c, i) =>
    typeof c === 'string' ? <span key={i}>{tokenize(c, verification)}</span> : c
  );
  return children;
}

function _verificationStatus(verification, token) {
  if (!verification?.citations) return null;
  const match = verification.citations.find((c) => c.token === token);
  return match?.status || null;
}

function _statusClass(status) {
  if (status === 'verified') return 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-800';
  if (status === 'unverified') return 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800';
  return 'text-teal-700 dark:text-teal-400';
}

function tokenize(text, verification) {
  const parts = [];
  const re = /(@doi:[^\s\]\),;]+|@pmid:[0-9]+|@tag:[^\s\]\),;]+)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[1];
    const status = _verificationStatus(verification, tok);
    const className = `font-mono text-[0.78em] px-1 py-0.5 rounded border ${_statusClass(status)}`;
    if (tok.startsWith('@doi:')) {
      const id = tok.slice(5);
      parts.push(
        <a key={i++} href={`https://doi.org/${id}`} target="_blank" rel="noopener noreferrer"
          className={className} title={status ? `Verification: ${status}` : ''}>
          {status === 'unverified' && '⚠ '}DOI:{id.slice(0, 20)}{id.length > 20 ? '…' : ''}
        </a>
      );
    } else if (tok.startsWith('@pmid:')) {
      const id = tok.slice(6);
      parts.push(
        <a key={i++} href={`https://pubmed.ncbi.nlm.nih.gov/${id}/`} target="_blank" rel="noopener noreferrer"
          className={className} title={status ? `Verification: ${status}` : ''}>
          {status === 'unverified' && '⚠ '}PMID:{id}
        </a>
      );
    } else {
      parts.push(<em key={i++} className={className.replace('text-teal-700', 'text-muted-foreground')}>[{tok.slice(5)}]</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function ExportCard({ icon, title, desc, href, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}>
      <Card className="hover:shadow-md transition cursor-pointer h-full">
        <CardContent className="pt-6 flex flex-col items-start gap-2">
          <div className="text-primary">{icon}</div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </CardContent>
      </Card>
    </a>
  );
}
